import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { deleteUser, resetUserPassword, type AdminUser } from '../../api/admin.ts';
import { toast } from '../../store/toast.ts';
import { ConfirmDialog } from '../common/ConfirmDialog.tsx';
import { Modal } from '../common/Modal.tsx';
import { IconCopy, IconKey, IconTrash } from '../../lib/icons.tsx';

interface Props {
  user: AdminUser;
  /** 지금 로그인한 관리자. 자기 계정에는 위험한 버튼을 보이지 않는다. */
  selfId: string;
  onChanged: () => void;
}

/**
 * 사용자 한 명에 대한 위험한 동작 — 비밀번호 초기화, 계정 삭제.
 *
 * 두 기능이 오래 빠져 있었다. 이 서비스에는 메일 발송이 없어서
 * "비밀번호 찾기" 가 존재하지 않고, 사용자가 스스로 탈퇴할 방법도 없다.
 * 관리자가 대신 하지 못하면 잊은 비밀번호는 영구 잠금이고, 쓰지 않는 계정은
 * 영구히 남는다.
 */
export function UserActions({ user, selfId, onChanged }: Props) {
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isSelf = user.id === selfId;

  const remove = useMutation({
    mutationFn: () => deleteUser(user.id),
    onSuccess: (r) => {
      toast.success(`${r.email} 계정을 삭제했습니다`);
      setConfirmDelete(false);
      onChanged();
    },
    onError: (e: Error) => { toast.error(e); setConfirmDelete(false); },
  });

  return (
    <>
      <button
        className="btn btn--sm"
        title="비밀번호 초기화"
        onClick={() => { setResetOpen(true); }}
      >
        <IconKey size={14} /> 비밀번호
      </button>
      <button
        className="btn btn--sm btn--danger"
        title={isSelf ? '자기 계정은 삭제할 수 없습니다' : '계정 삭제'}
        disabled={isSelf}
        onClick={() => { setConfirmDelete(true); }}
      >
        <IconTrash size={14} />
      </button>

      {resetOpen && (
        <ResetPasswordModal
          user={user}
          onClose={() => { setResetOpen(false); }}
          onDone={onChanged}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="계정을 삭제할까요?"
          message={`${user.email} (${user.display_name}) 계정과 그 안의 모든 파일이 `
            + '영구히 사라집니다. 휴지통으로도 가지 않습니다.\n\n되돌릴 수 없습니다.'}
          confirmLabel="영구 삭제"
          danger
          busy={remove.isPending}
          onConfirm={() => { remove.mutate(); }}
          onCancel={() => { setConfirmDelete(false); }}
        />
      )}
    </>
  );
}

/**
 * 임시 비밀번호를 정해 알려주는 창.
 *
 * 만든 비밀번호를 화면에 남겨 두는 이유: 메일을 보낼 수 없으므로 관리자가
 * 사용자에게 직접 전달해야 한다. 창을 닫으면 다시 볼 수 없다(해시만 저장).
 */
function ResetPasswordModal(
  { user, onClose, onDone }: { user: AdminUser; onClose: () => void; onDone: () => void },
) {
  const [password, setPassword] = useState(() => suggestPassword());
  const [done, setDone] = useState(false);

  const reset = useMutation({
    mutationFn: () => resetUserPassword(user.id, password),
    onSuccess: (r) => {
      toast.success(`비밀번호를 바꿨습니다 (로그인 ${r.revoked_sessions}건 해제)`);
      setDone(true);
      onDone();
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('복사했습니다');
    } catch {
      toast.error('복사할 수 없습니다. 직접 선택해 복사해 주세요.');
    }
  };

  return (
    <Modal
      title={done ? '비밀번호를 바꿨습니다' : `${user.display_name} 비밀번호 초기화`}
      onClose={onClose}
      footer={done ? (
        <button className="btn btn--primary" onClick={onClose}>확인했습니다</button>
      ) : (
        <>
          <button className="btn" onClick={onClose}>취소</button>
          <button
            className="btn btn--primary"
            disabled={password.length < 10 || reset.isPending}
            onClick={() => { reset.mutate(); }}
          >
            {reset.isPending ? <span className="spinner" /> : '바꾸기'}
          </button>
        </>
      )}
    >
      {!done && (
        <>
          <p className="panel__hint" style={{ marginTop: 0 }}>
            이 서비스는 메일을 보내지 않습니다. 새 비밀번호를 사용자에게 직접
            알려주세요. 바꾸면 그 계정의 모든 로그인과 앱 비밀번호가 끊깁니다.
          </p>
          <div className="field">
            <label htmlFor="rp">새 비밀번호 (10자 이상)</label>
            <div className="row" style={{ gap: 8 }}>
              <input
                id="rp" className="input spacer" type="text" autoComplete="off"
                value={password} onChange={(e) => { setPassword(e.target.value); }}
              />
              <button
                className="btn" type="button"
                onClick={() => { setPassword(suggestPassword()); }}
              >
                다시 만들기
              </button>
            </div>
          </div>
        </>
      )}

      {done && (
        <>
          <p className="panel__hint" style={{ marginTop: 0 }}>
            아래 비밀번호를 <b>{user.email}</b> 님에게 전달하세요.
            이 창을 닫으면 다시 볼 수 없습니다.
          </p>
          <div className="codeline">
            <code className="issued__pw">{password}</code>
            <button className="btn btn--sm" onClick={() => { void copy(); }}>
              <IconCopy size={14} /> 복사
            </button>
          </div>
          <p className="panel__hint">
            첫 로그인 뒤에 사용자가 직접 바꾸도록 안내해 주세요.
          </p>
        </>
      )}
    </Modal>
  );
}

/**
 * 옮겨 적기 쉬운 임시 비밀번호를 만든다.
 *
 * 관리자가 말이나 메신저로 전달하는 값이라, 혼동되는 글자(0/o, 1/l/i)를 빼고
 * 4글자씩 끊어 준다. 서버 정책(10자 이상)을 넉넉히 넘긴다.
 */
function suggestPassword(): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  const chars = Array.from(bytes, (b) => alphabet[b % alphabet.length]);
  return `${chars.slice(0, 4).join('')}-${chars.slice(4, 8).join('')}-${chars.slice(8, 12).join('')}`;
}
